import React, { useState } from 'react';
import {
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  FileText,
  MessageSquare,
  Link,
  Phone,
  Info,
  ExternalLink,
  ImageIcon,
  VideoIcon,
  FileText as FileIcon,
  Type,
  LayoutTemplate,
  AlignLeft,
  MousePointer2,
  Copy,
  Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { FormCard } from '@/components';
import { useTemplate } from '@/features/chatbots';
import { AUTH_BODY_TRANSLATIONS } from '@/features/whats-app-crm/utils/language';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { Loader } from '@/components';
import { formatDateTime } from '@/utils/dateFormatter';


// ─── Types ───────────────────────────────────────────────────────────────────

interface MetaButton {
  type: 'quick_reply' | 'url' | 'phone_number' | 'otp' | 'copy_code' | 'flow' | 'voice_call';
  text?: string;
  url?: string;
  phone_number?: string;
  otp_type?: string;
  example?: string;
  label?: string;
  title?: string;
}

interface MetaComponent {
  type: 'header' | 'body' | 'footer' | 'buttons';
  format?: 'text' | 'image' | 'video' | 'document';
  text?: string;
  buttons?: MetaButton[];
  add_security_recommendation?: boolean;
  example?: {
    body_text?: string[][];
    header_handle?: string[];
    header_text?: string[] | string[][];
    header_text_named_params?: Array<{ param_name: string; example: string }> | Array<Array<{ param_name: string; example: string }>>;
  };
}

const getPlaceholderKey = (placeholder: string) => placeholder.replace(/{{|}}/g, '').trim();

const normalizeNamedParams = (
  params?: Array<{ param_name: string; example: string }> | Array<Array<{ param_name: string; example: string }>>
) => {
  if (!params) return [];
  return Array.isArray(params[0])
    ? (params[0] as Array<{ param_name: string; example: string }>)
    : (params as Array<{ param_name: string; example: string }>);
};

const buildExampleMap = (
  matches: string[],
  positionalExamples?: string[],
  namedParams?: Array<{ param_name: string; example: string }> | Array<Array<{ param_name: string; example: string }>>
) => {
  const exampleMap: Record<string, string> = {};
  const uniquePositionKeys: string[] = [];

  matches.forEach((match) => {
    const key = getPlaceholderKey(match);
    if (!exampleMap[key] && /^\d+$/.test(key)) {
      if (!uniquePositionKeys.includes(key)) {
        uniquePositionKeys.push(key);
      }
      const example = positionalExamples?.[Number(key) - 1] ?? positionalExamples?.[uniquePositionKeys.indexOf(key)];
      if (example) {
        exampleMap[key] = example;
      }
    }
  });

  normalizeNamedParams(namedParams).forEach((param) => {
    if (param?.param_name && param.example) {
      exampleMap[param.param_name] = param.example;
    }
  });

  return exampleMap;
};

function getAuthBodyText(language: string, withSecurity: boolean): string {
  const translations = AUTH_BODY_TRANSLATIONS[language] || AUTH_BODY_TRANSLATIONS['en'];
  return withSecurity ? translations.withSecurity : translations.default;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(date?: string): string {
  if (!date) return '—';
  return formatDateTime(date);
}

function parseComponents(components?: MetaComponent[], category: string = '', language: string = 'en') {
  if (!components) return { header: undefined, body: undefined, footer: undefined, buttons: [], bodyText: '', variables: [], headerVariables: [] };

  const isAuth = category.toUpperCase() === 'AUTHENTICATION';
  const header = components.find((c) => c.type?.toLowerCase() === 'header');
  const body = components.find((c) => c.type?.toLowerCase() === 'body');
  const footer = components.find((c) => c.type?.toLowerCase() === 'footer');
  const buttonsComp = components.find((c) => c.type?.toLowerCase() === 'buttons');

  const bodyText = isAuth
    ? (body?.text || getAuthBodyText(language, Boolean(body?.add_security_recommendation)))
    : body?.text ?? '';
  const variableMatches = isAuth ? [] : bodyText.match(/{{[^}]+}}/g) ?? [];
  const bodyExampleMap = buildExampleMap(
    variableMatches,
    body?.example?.body_text?.[0],
    (body?.example as any)?.body_text_named_params
  );
  const variables = variableMatches.map((match) => {
    const key = getPlaceholderKey(match);
    return {
      name: match,
      key,
      fallback: bodyExampleMap[key] ?? '',
    };
  });

  const headerText = header?.text ?? '';
  const headerVariableMatches = headerText.match(/{{[^}]+}}/g) ?? [];
  const headerExample = header?.example as any;
  const headerTextExample = headerExample?.header_text?.[0];
  const headerExampleMap = buildExampleMap(
    headerVariableMatches,
    Array.isArray(headerTextExample) ? headerTextExample : headerTextExample ? [headerTextExample] : undefined,
    headerExample?.header_text_named_params
  );
  const headerVariables = headerVariableMatches.map((match) => {
    const key = match.replace(/{{|}}/g, '').trim();
    return { name: match, key, fallback: headerExampleMap[key] ?? '' };
  });

  const headerValueMap = headerVariables.reduce<Record<string, string>>((acc, variable) => {
    if (variable.key) {
      acc[variable.key] = variable.fallback;
    }
    return acc;
  }, {});

  const headerPreviewText = headerText.replace(/{{([^}]+)}}/g, (match, rawKey) => {
    const key = rawKey.trim();
    return headerValueMap[key] || match;
  });

  const buttons = buttonsComp?.buttons?.map((btn) => ({
    ...btn,
    text:
      btn.text ||
      btn.title ||
      btn.label ||
      (btn.type === 'otp' || btn.type === 'copy_code' ? 'Copy code' : ''),
  })) ?? [];

  return {
    header,
    body,
    footer,
    buttons,
    bodyText,
    variables,
    headerVariables,
    headerPreviewText,
  };
}


// ─── Sub-components ──────────────────────────────────────────────────────────

const statusConfig = {
  draft: {
    label: 'Draft',
    icon: FileText,
    bannerClass: 'bg-ui-surface-soft border-ui-hairline',
    iconBg: 'bg-ui-muted',
    textClass: 'text-ui-ink',
    descClass: 'text-ui-muted',
    description: 'Template is in draft mode and can be edited',
    badgeClass: 'bg-ui-surface-soft text-ui-body border border-ui-hairline',
  },
  submitted: {
    label: 'Pending Review',
    icon: Clock,
    bannerClass: 'bg-ui-primary-light border-ui-primary-light',
    iconBg: 'bg-ui-primary',
    textClass: 'text-ui-primary',
    descClass: 'text-ui-primary',
    description: 'Template submitted to Meta — awaiting approval',
    badgeClass: 'bg-ui-primary-light text-ui-primary border border-ui-primary/20',
  },
  approved: {
    label: 'Approved',
    icon: CheckCircle,
    bannerClass: 'bg-ui-success/10 border-ui-success/20',
    iconBg: 'bg-ui-success',
    textClass: 'text-ui-success',
    descClass: 'text-ui-success',
    description: 'Approved by Meta — ready to send to users',
    badgeClass: 'bg-ui-success/10 text-ui-success border border-ui-success/25',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    bannerClass: 'bg-ui-error/10 border-ui-error/20',
    iconBg: 'bg-ui-error',
    textClass: 'text-ui-error',
    descClass: 'text-ui-error',
    description: 'Rejected by Meta — fix issues and resubmit',
    badgeClass: 'bg-ui-error/10 text-ui-error border border-ui-error/25',
  },
  disabled: {
    label: 'Disabled',
    icon: AlertCircle,
    bannerClass: 'bg-ui-surface-soft border-ui-hairline',
    iconBg: 'bg-ui-muted',
    textClass: 'text-ui-muted',
    descClass: 'text-ui-muted',
    description: 'Template has been disabled',
    badgeClass: 'bg-ui-surface-soft text-ui-muted border border-ui-hairline',
  },
} as const;

const buttonTypeConfig = {
  quick_reply: { label: 'Quick Reply', icon: MessageSquare, colorClass: 'bg-ui-primary-light text-ui-primary border-ui-hairline' },
  url: { label: 'URL', icon: Link, colorClass: 'bg-ui-primary-light text-ui-primary border-ui-hairline' },
  phone_number: { label: 'Phone', icon: Phone, colorClass: 'bg-ui-primary-light text-ui-primary border-ui-hairline' },
  otp: { label: 'OTP', icon: Copy, colorClass: 'bg-ui-primary-light text-ui-primary border-ui-hairline' },
  copy_code: { label: 'Copy Code', icon: Copy, colorClass: 'bg-ui-primary-light text-ui-primary border-ui-hairline' },
};



// ─── Components Breakdown ─────────────────────────────────────────────────────

function ComponentsBreakdown({ components }: { components: ReturnType<typeof parseComponents> }) {
  const { header, bodyText, footer, buttons, variables, headerVariables, headerPreviewText } = components;

  const getHeaderIcon = () => {
    switch (header?.format?.toLowerCase()) {
      case 'image': return { icon: ImageIcon, bg: 'bg-ui-success/10', text: 'text-ui-success' };
      case 'video': return { icon: VideoIcon, bg: 'bg-ui-primary-light', text: 'text-ui-primary' };
      case 'document': return { icon: FileIcon, bg: 'bg-ui-primary-light', text: 'text-ui-primary' };
      default: return { icon: Type, bg: 'bg-ui-surface-soft', text: 'text-ui-muted' };
    }
  };

  const hCfg = getHeaderIcon();

  return (
    <div className="space-y-4">
      {/* Header */}
      {header && (
        <FormCard
          title="Header"
          description="Media or text shown at the top of the message."
          icon={LayoutTemplate}
          action={
            <span className="text-[10px] bg-ui-surface-soft text-ui-muted px-2 py-0.5 rounded-sm border border-ui-hairline font-bold uppercase tracking-widest">
              {header.format ?? 'text'}
            </span>
          }
        >
          {header.text ? (
            <>
              <div className="p-3 bg-ui-surface-soft rounded-sm border border-ui-hairline-soft">
                <p className="text-[14px] font-medium text-ui-ink leading-relaxed">{headerPreviewText || header.text}</p>
              </div>
              {headerVariables?.length > 0 && (
                <div className="mt-4 space-y-3">
                  <p className="text-[11px] font-bold text-ui-muted uppercase tracking-widest px-1">Header Variable Examples</p>
                  <div className="rounded-sm bg-ui-surface-soft border border-ui-hairline-soft overflow-hidden">
                    <table className="w-full text-left text-[12px]">
                      <thead>
                        <tr className="bg-ui-surface-soft border-b border-ui-hairline-soft">
                          <th className="px-4 py-3 font-bold text-ui-muted uppercase tracking-widest">Variable</th>
                          <th className="px-4 py-3 font-bold text-ui-muted uppercase tracking-widest">Example Value</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-ui-hairline-soft">
                        {headerVariables.map((v, i) => (
                          <tr key={i} className="hover:bg-ui-surface-soft/40 transition-colors">
                            <td className="px-4 py-3 font-mono text-ui-primary font-bold">{v.name}</td>
                            <td className="px-4 py-3 text-ui-body font-medium">
                              {v.fallback || <em className="text-ui-muted opacity-40">Not defined</em>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 p-4 bg-ui-surface-soft rounded-sm text-ui-muted text-sm italic border border-ui-hairline-soft">
              <hCfg.icon className="w-5 h-5 opacity-30" />
              The header contains {header.format} media example
            </div>
          )}
        </FormCard>
      )}

      {/* Body */}
      <FormCard
        title="Body Message"
        description="The primary content of your template message."
        icon={AlignLeft}
        action={
          variables.length > 0 && (
            <span className="text-[10px] bg-ui-primary-light text-ui-primary px-2 py-0.5 rounded-sm font-bold uppercase tracking-widest border border-ui-primary/20">
              {variables.length} variable{variables.length > 1 ? 's' : ''}
            </span>
          )
        }
      >
        <div className="space-y-4">
          <div className="p-4 bg-ui-surface-card rounded-sm border border-ui-hairline-soft">
            <p className="text-sm text-ui-body leading-relaxed break-words whitespace-pre-wrap">
              {bodyText.split(/({{[^}]+}})/).map((part, i) =>
                /^{{[^}]+}}$/.test(part) ? (
                  <span key={i} className="bg-ui-primary-light text-ui-primary px-1.5 py-0.5 rounded-sm font-mono text-[13px] font-bold">
                    {part}
                  </span>
                ) : (
                  <span key={i}>{part}</span>
                )
              )}
            </p>
          </div>

          {variables.length > 0 && (
            <div className="space-y-3">
              <p className="text-[11px] font-bold text-ui-muted uppercase tracking-widest px-1">Variable Examples</p>
              <div className="rounded-sm bg-ui-surface-soft border border-ui-hairline-soft overflow-hidden">
                <table className="w-full text-left text-[12px]">
                  <thead>
                    <tr className="bg-ui-surface-soft border-b border-ui-hairline-soft">
                      <th className="px-4 py-3 font-bold text-ui-muted uppercase tracking-widest">Variable</th>
                      <th className="px-4 py-3 font-bold text-ui-muted uppercase tracking-widest">Example Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ui-hairline-soft">
                    {variables.map((v, i) => (
                      <tr key={i} className="hover:bg-ui-surface-soft/40 transition-colors">
                        <td className="px-4 py-3 font-mono text-ui-primary font-bold">{v.name}</td>
                        <td className="px-4 py-3 text-ui-body font-medium">{v.fallback || <em className="text-ui-muted opacity-40">Not defined</em>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </FormCard>

      {/* Footer & Buttons Group */}
      <div className="flex flex-col gap-4">
        {footer?.text && (
          <FormCard
            title="Footer"
            description="Optional Text"
            icon={Type}
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="text-center p-3 bg-ui-surface-soft rounded-sm border border-ui-hairline-soft">
                <p className="text-sm text-ui-muted font-medium leading-relaxed">{footer.text}</p>
              </div>
            </div>
          </FormCard>
        )}

        {buttons.length > 0 && (
          <FormCard
            title="Buttons"
            description="Interaction points for users."
            icon={MousePointer2}
            action={
              <span className="text-[10px] font-bold text-ui-muted uppercase tracking-widest">{buttons.length} Total</span>
            }
          >
            <div className="space-y-2.5">
              {buttons.map((btn, i) => {
                const cfg = buttonTypeConfig[btn.type as keyof typeof buttonTypeConfig] ?? { label: btn.type, icon: MessageSquare, colorClass: 'bg-ui-surface-soft text-ui-muted border-ui-hairline' };
                const BtnIcon = cfg.icon;
                return (
                  <div key={i} className={`flex items-center justify-between px-3.5 py-3 rounded-sm border transition-all shadow-none ${cfg.colorClass}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-1.5 bg-ui-surface-soft/50 rounded-sm">
                        <BtnIcon className="w-4 h-4 shrink-0" />
                      </div>
                      <span className="text-sm font-bold truncate">{btn.text}</span>
                    </div>
                    {btn.url && (
                      <a href={btn.url} target="_blank" rel="noreferrer" className="shrink-0 p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-sm transition-colors">
                        <ExternalLink className="w-4 h-4 opacity-60" />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </FormCard>
        )}
      </div>
    </div>
  );
}



function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-ui-hairline-soft last:border-b-0">
      <span className="text-xs text-ui-muted">{label}</span>
      <span className="text-xs font-medium text-ui-body">{value}</span>
    </div>
  );
}

function CopyIdRow({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(id).then(() => {
      setCopied(true);
      toast.success('Template ID copied!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-ui-hairline-soft">
      <span className="text-xs text-ui-muted">Template ID</span>
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-mono text-ui-muted" title={id}>
          {id.slice(0, 5)}....{id.slice(-5)}
        </span>
        <button
          onClick={handleCopy}
          className="p-1 rounded-sm transition-all text-ui-muted hover:text-ui-ink hover:bg-ui-surface-soft"
          title={copied ? 'Copied!' : 'Copy full ID'}
        >
          {copied ? (
            <Check className="w-3 h-3 text-ui-success" />
          ) : (
            <Copy className="w-3 h-3" />
          )}
        </button>
      </div>
    </div>
  );
}



export default function ViewTemplatePage() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { setHeaderActions, setCustomHeader, setWhatsAppValues } = useOutletContext<any>() || {};
  const { data: templateData, isFetching } = useTemplate(
    templateId || null,
    { enabled: Boolean(templateId) }
  );

  const template = templateData && templateData?.data;

  const category = (template?.category || template?.metaPayload?.category || '').toString();
  const components = templateData && parseComponents(
    template?.metaPayload?.components,
    category,
    template?.language || template?.metaPayload?.language || 'en'
  );
  const status = template && statusConfig[template?.status!];

  React.useEffect(() => {
    if (!setCustomHeader || !setHeaderActions) return;

    if (template) {
      setCustomHeader({
        heading: template.metaTemplateName,
        description: `v${template.version} · ${template.category} · ${template.language}`,
        showBackButton: true,
        onBack: () => navigate(-1),
      });
    } else {
      setCustomHeader({
        heading: "Loading Template...",
        description: "Fetching details",
        showBackButton: true,
        onBack: () => navigate(-1),
      });
    }

    if (setWhatsAppValues && templateData?.data) {
      setWhatsAppValues(templateData.data);
    }

    return () => {
      setCustomHeader?.(null);
      setHeaderActions?.(null);
    };
  }, [setCustomHeader, setHeaderActions, template, navigate, templateData, setWhatsAppValues]);



  if (isFetching) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <AlertCircle className="w-6 h-6 text-ui-error" />
        <p className="ml-2 text-sm text-ui-muted">Template not found</p>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-ui-canvas font-sans">
      <div className="flex flex-col gap-4">


        {/* Rejection Reason */}
        {template?.rejectionReason && (
          <div className="rounded-sm border border-ui-error/30 overflow-hidden shadow-none">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-ui-error/10 border-b border-ui-error/20">
              <XCircle className="w-4 h-4 text-ui-error" />
              <span className="text-xs font-semibold text-ui-error uppercase tracking-wider">Rejection Reason from Meta</span>
            </div>
            <div className="px-4 py-3 bg-ui-surface-card">
              <p className="text-sm text-ui-error leading-relaxed">{template.rejectionReason}</p>
            </div>
          </div>
        )}

        {/* Two-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">

          {/* Left column */}
          <div>
            <ComponentsBreakdown components={components!} />
          </div>

          {/* Right sidebar */}
          <div className="space-y-4">

            {/* Details */}
            <FormCard
              title="Details"
              description="Metadata"
              icon={Info}
              className="overflow-hidden"
              contentClassName="p-0"
            >
              <CopyIdRow id={template._id} />
              <DetailRow label="Category" value={<span className="capitalize">{template?.category}</span>} />
              <DetailRow label="Language" value={<span className="uppercase">{template?.language}</span>} />
              <DetailRow label="Version" value={`v${template?.version}`} />
              {template?.metaStatus && (
                <DetailRow
                  label="Meta Status"
                  value={
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold ${status?.badgeClass}`}>
                      {template.metaStatus}
                    </span>
                  }
                />
              )}
            </FormCard>

            {/* Timeline */}
            <FormCard
              title="Timeline"
              description="History"
              icon={Clock}
            >
              <div className="relative pl-6">
                <div className="absolute left-[3px] top-1 top-1 bottom-1 w-[2px] bg-ui-hairline-soft" />
                {[
                  { label: 'Created', date: template?.createdAt, dot: 'bg-ui-muted' },
                  ...(template?.submittedAt ? [{ label: 'Submitted to Meta', date: template?.submittedAt, dot: 'bg-ui-primary' }] : []),
                  { label: 'Last Updated', date: template?.updatedAt, dot: 'bg-ui-primary' },
                  ...(template?.approvedAt ? [{ label: 'Approved by Meta', date: template?.approvedAt, dot: 'bg-ui-success' }] : []),
                ].map(({ label, date, dot }, i) => (
                  <div key={i} className="flex items-start gap-4 mb-4 last:mb-0 relative">
                    <div className={`absolute -left-[27px] top-1.5 w-2.5 h-2.5 rounded-full ${dot} border-2 border-ui-surface-card z-10 shadow-none`} />
                    <div>
                      <p className="text-[12px] font-bold text-ui-ink leading-none">{label}</p>
                      <p className="text-[10px] text-ui-muted mt-1.5 font-medium">{formatDate(date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </FormCard>

          </div>
        </div>
      </div>
    </div>
  );
}


